// Contact2.js - Contains functions for creating an enhanced contact section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates an enhanced Contact section with a form, contact information, map, and social links
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the contact section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const contactSectionClass = 'contact2-section';
  const contactContainerClass = 'contact2-container';
  const contactHeaderClass = 'contact2-header';
  const contactTitleClass = 'contact2-title';
  const contactSubtitleClass = 'contact2-subtitle';
  const contactContentClass = 'contact2-content';
  const contactLeftColumnClass = 'contact2-left-column';
  const contactRightColumnClass = 'contact2-right-column';
  
  // Info classes
  const contactInfoClass = 'contact2-info';
  const contactInfoItemClass = 'contact2-info-item';
  const contactInfoIconClass = 'contact2-info-icon';
  const contactInfoTextClass = 'contact2-info-text';
  const contactInfoTitleClass = 'contact2-info-title';
  
  // Form classes
  const contactFormWrapperClass = 'contact2-form-wrapper';
  const contactFormTitleClass = 'contact2-form-title';
  const contactFormClass = 'contact2-form';
  const contactFormRowClass = 'contact2-form-row';
  const contactFormGroupClass = 'contact2-form-group';
  const contactFormLabelClass = 'contact2-form-label';
  const contactFormInputClass = 'contact2-form-input';
  const contactFormTextareaClass = 'contact2-form-textarea';
  const contactFormSelectClass = 'contact2-form-select';
  const contactFormCheckboxClass = 'contact2-form-checkbox';
  const contactFormButtonClass = 'contact2-form-button';
  const contactFormSuccessClass = 'contact2-form-success';
  const contactFormErrorClass = 'contact2-form-error';
  
  // Map and social classes
  const contactMapContainerClass = 'contact2-map-container';
  const contactMapFrameClass = 'contact2-map-frame';
  const contactSocialContainerClass = 'contact2-social-container';
  const contactSocialTitleClass = 'contact2-social-title';
  const contactSocialLinksClass = 'contact2-social-links';
  const contactSocialLinkClass = 'contact2-social-link';
  const contactSocialIconClass = 'contact2-social-icon';
  
  // Desktop Styles (default)
  const contactSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '30px',
    paddingRight: '30px',
    backgroundColor: '#f9fafb',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: 'linear-gradient(120deg, #fdfbfb 0%, #ebedee 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  const contactContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1280px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '70px'
  };

  const contactHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '30px',
    width: '100%',
    maxWidth: '800px'
  };

  const contactTitleStyles = {
    fontSize: '42px',
    fontWeight: 'bold',
    color: '#1a202c',
    marginBottom: '20px',
    letterSpacing: '-0.025em',
    fontFamily: 'Georgia, serif'
  };

  const contactSubtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    lineHeight: '1.7',
    marginBottom: '35px',
    maxWidth: '700px'
  };

  const contactContentStyles = {
    display: 'flex',
    width: '100%',
    gap: '50px',
    justifyContent: 'space-between',
    alignItems: 'stretch'
  };

  const contactLeftColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    flex: '1',
    maxWidth: '650px'
  };

  const contactRightColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    flex: '1',
    maxWidth: '500px'
  };

  const contactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '35px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0'
  };

  const contactInfoItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px'
  };

  const contactInfoIconStyles = {
    width: '45px',
    height: '45px',
    borderRadius: '10px',
    backgroundColor: '#4f46e5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: '20px',
    padding: '10px',
    boxShadow: '0 4px 6px rgba(79, 70, 229, 0.2)'
  };

  const contactInfoTextStyles = {
    fontSize: '16px',
    color: '#4b5563',
    lineHeight: '1.6'
  };

  const contactInfoTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '8px'
  };

  const contactFormWrapperStyles = {
    flex: '1',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
    padding: '40px',
    border: '1px solid #e2e8f0'
  };

  const contactFormTitleStyles = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a202c',
    marginBottom: '25px'
  };

  const contactFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    width: '100%'
  };

  const contactFormRowStyles = {
    display: 'flex',
    gap: '20px',
    width: '100%'
  };

  const contactFormGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  };

  const contactFormLabelStyles = {
    fontSize: '15px',
    fontWeight: '500',
    color: '#4b5563'
  };

  const contactFormInputStyles = {
    width: '100%',
    height: '40px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '16px',
    color: '#1a202c',
    backgroundColor: '#f8fafc',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormTextareaStyles = {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '16px',
    color: '#1a202c',
    backgroundColor: '#f8fafc',
    minHeight: '150px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormSelectStyles = {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '16px',
    color: '#1a202c',
    backgroundColor: '#f8fafc',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23131313%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px top 50%',
    backgroundSize: '12px auto',
    transition: 'all 0.3s ease'
  };

  const contactFormCheckboxStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '5px'
  };

  const contactFormButtonStyles = {
    padding: '14px 28px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    marginTop: '15px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(79, 70, 229, 0.2)'
  };

  const contactFormSuccessStyles = {
    padding: '16px',
    backgroundColor: '#ecfdf5',
    color: '#047857',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #a7f3d0'
  };

  const contactFormErrorStyles = {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #fecaca'
  };

  const contactMapContainerStyles = {
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
    height: '300px',
    border: '1px solid #e2e8f0'
  };

  const contactMapFrameStyles = {
    width: '100%',
    height: '100%',
    border: '0'
  };

  const contactSocialContainerStyles = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0'
  };

  const contactSocialTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '10px'
  };

  const contactSocialLinksStyles = {
    display: 'flex',
    gap: '15px',
    alignItems: 'center'
  };

  const contactSocialLinkStyles = {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: '#f7fafc',
    color: '#4f46e5',
    transition: 'all 0.3s ease',
    border: '1px solid #e2e8f0'
  };

  const contactSocialIconStyles = {
    width: '20px',
    height: '20px'
  };
  
  // Tablet Styles
  const tabletContactContentStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px'
  };
  
  const tabletContactLeftColumnStyles = {
    maxWidth: '100%'
  };
  
  const tabletContactRightColumnStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  const tabletContactFormRowStyles = {
    flexDirection: 'column',
    gap: '15px'
  };
  
  const tabletContactTitleStyles = {
    fontSize: '36px'
  };
  
  // Mobile Styles
  const mobileContactSectionStyles = {
    paddingTop: '70px',
    paddingBottom: '70px',
    paddingLeft: '20px',
    paddingRight: '20px'
  };
  
  const mobileContactHeaderStyles = {
    marginBottom: '20px'
  };
  
  const mobileContactTitleStyles = {
    fontSize: '32px',
    marginBottom: '15px'
  };
  
  const mobileContactSubtitleStyles = {
    fontSize: '16px',
    marginBottom: '25px'
  };
  
  const mobileContactFormWrapperStyles = {
    padding: '30px 20px'
  };
  
  const mobileContactMapContainerStyles = {
    height: '250px'
  };
  
  const mobileContactInfoIconStyles = {
    width: '40px',
    height: '40px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(contactSectionClass, contactSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContainerClass, contactContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactHeaderClass, contactHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(contactTitleClass, contactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSubtitleClass, contactSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContentClass, contactContentStyles);
    newRegistry[DEVICES.DESKTOP].set(contactLeftColumnClass, contactLeftColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(contactRightColumnClass, contactRightColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoClass, contactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoItemClass, contactInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoIconClass, contactInfoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoTextClass, contactInfoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoTitleClass, contactInfoTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormWrapperClass, contactFormWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormTitleClass, contactFormTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormClass, contactFormStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormRowClass, contactFormRowStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormGroupClass, contactFormGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormLabelClass, contactFormLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormInputClass, contactFormInputStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormTextareaClass, contactFormTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormSelectClass, contactFormSelectStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormCheckboxClass, contactFormCheckboxStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormButtonClass, contactFormButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormSuccessClass, contactFormSuccessStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormErrorClass, contactFormErrorStyles);
    newRegistry[DEVICES.DESKTOP].set(contactMapContainerClass, contactMapContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactMapFrameClass, contactMapFrameStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSocialContainerClass, contactSocialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSocialTitleClass, contactSocialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSocialLinksClass, contactSocialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSocialLinkClass, contactSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSocialIconClass, contactSocialIconStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contactContentClass, tabletContactContentStyles);
    newRegistry[DEVICES.TABLET].set(contactLeftColumnClass, tabletContactLeftColumnStyles);
    newRegistry[DEVICES.TABLET].set(contactRightColumnClass, tabletContactRightColumnStyles);
    newRegistry[DEVICES.TABLET].set(contactFormRowClass, tabletContactFormRowStyles);
    newRegistry[DEVICES.TABLET].set(contactTitleClass, tabletContactTitleStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(contactSectionClass, mobileContactSectionStyles);
    newRegistry[DEVICES.MOBILE].set(contactHeaderClass, mobileContactHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(contactTitleClass, mobileContactTitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactSubtitleClass, mobileContactSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactFormWrapperClass, mobileContactFormWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(contactMapContainerClass, mobileContactMapContainerStyles);
    newRegistry[DEVICES.MOBILE].set(contactInfoIconClass, mobileContactInfoIconStyles);
    
    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    contactSectionClass,
    contactContainerClass,
    contactHeaderClass,
    contactTitleClass,
    contactSubtitleClass,
    contactContentClass,
    contactLeftColumnClass,
    contactRightColumnClass,
    contactInfoClass,
    contactInfoItemClass,
    contactInfoIconClass,
    contactInfoTextClass,
    contactInfoTitleClass,
    contactFormWrapperClass,
    contactFormTitleClass,
    contactFormClass,
    contactFormRowClass,
    contactFormGroupClass,
    contactFormLabelClass,
    contactFormInputClass,
    contactFormTextareaClass,
    contactFormSelectClass,
    contactFormCheckboxClass,
    contactFormButtonClass,
    contactFormSuccessClass,
    contactFormErrorClass,
    contactMapContainerClass,
    contactMapFrameClass,
    contactSocialContainerClass,
    contactSocialTitleClass,
    contactSocialLinksClass,
    contactSocialLinkClass,
    contactSocialIconClass
  ]));
  
  // Generate IDs for elements
  // Main structure
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Content container
  const contentId = Date.now() + 5;
  const leftColumnId = Date.now() + 6;
  const rightColumnId = Date.now() + 7;
  
  // Contact information elements
  const infoContainerId = Date.now() + 8;
  
  // Address info
  const addressItemId = Date.now() + 9;
  const addressIconId = Date.now() + 10;
  const addressTextId = Date.now() + 11;
  const addressTitleId = Date.now() + 12;
  const addressLine1Id = Date.now() + 13;
  const addressLine2Id = Date.now() + 14;
  
  // Phone info
  const phoneItemId = Date.now() + 15;
  const phoneIconId = Date.now() + 16;
  const phoneTextId = Date.now() + 17;
  const phoneTitleId = Date.now() + 18;
  const phoneMainId = Date.now() + 19;
  const phoneHoursId = Date.now() + 20;
  
  // Email info
  const emailItemId = Date.now() + 21;
  const emailIconId = Date.now() + 22;
  const emailTextId = Date.now() + 23;
  const emailTitleId = Date.now() + 24;
  const emailMainId = Date.now() + 25;
  const emailSupportId = Date.now() + 26;
  
  // Map element
  const mapContainerId = Date.now() + 27;
  const mapFrameId = Date.now() + 28;
  
  // Social Media
  const socialContainerId = Date.now() + 29;
  const socialTitleId = Date.now() + 30;
  const socialLinksId = Date.now() + 31;
  const facebookLinkId = Date.now() + 32;
  const facebookIconId = Date.now() + 33;
  const twitterLinkId = Date.now() + 34;
  const twitterIconId = Date.now() + 35;
  const instagramLinkId = Date.now() + 36;
  const instagramIconId = Date.now() + 37;
  const linkedinLinkId = Date.now() + 38;
  const linkedinIconId = Date.now() + 39;
  
  // Form elements
  const formWrapperId = Date.now() + 40;
  const formTitleId = Date.now() + 41;
  const formId = Date.now() + 42;
  
  // Name and email row
  const nameEmailRowId = Date.now() + 43;
  
  // Name field
  const nameGroupId = Date.now() + 44;
  const nameLabelId = Date.now() + 45;
  const nameInputId = Date.now() + 46;
  
  // Email field
  const emailGroupId = Date.now() + 47;
  const emailLabelId = Date.now() + 48;
  const emailInputId = Date.now() + 49;
  
  // Phone and subject row
  const phoneSubjectRowId = Date.now() + 50;
  
  // Phone field
  const phoneGroupId = Date.now() + 51;
  const phoneLabelId = Date.now() + 52;
  const phoneInputId = Date.now() + 53;
  
  // Subject field
  const subjectGroupId = Date.now() + 54;
  const subjectLabelId = Date.now() + 55;
  const subjectSelectId = Date.now() + 56;
  const option1Id = Date.now() + 57;
  const option2Id = Date.now() + 58;
  const option3Id = Date.now() + 59;
  const option4Id = Date.now() + 60;
  
  // Message field
  const messageGroupId = Date.now() + 61;
  const messageLabelId = Date.now() + 62;
  const messageTextareaId = Date.now() + 63;
  
  // Checkbox field
  const checkboxGroupId = Date.now() + 64;
  const checkboxInputId = Date.now() + 65;
  const checkboxLabelId = Date.now() + 66;
  
  // Submit button and status messages
  const submitButtonId = Date.now() + 67;
  const successMessageId = Date.now() + 68;
  const errorMessageId = Date.now() + 69;
  
  const newElements = [
    // Main Contact Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Contact Section 2',
      parentId: null,
      className: contactSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'contact-section',
        'data-component': 'section',
        'data-section-type': 'contact',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: contactContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'contact-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'contact-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: contactTitleClass,
      styles: {},
      children: [],
      text: 'Get in Touch With Our Team',
      attributes: {
        'data-element': 'contact-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: contactSubtitleClass,
      styles: {},
      children: [],
      text: 'We value your feedback and inquiries. Our dedicated team is ready to assist you with any questions, concerns, or collaboration opportunities you may have.',
      attributes: {
        'data-element': 'contact-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactContentClass,
      styles: {},
      children: [leftColumnId, rightColumnId],
      text: '',
      attributes: {
        'data-element': 'contact-content'
      }
    },
    
    // Left Column
    {
      id: leftColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Left Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactLeftColumnClass,
      styles: {},
      children: [infoContainerId, mapContainerId, socialContainerId],
      text: '',
      attributes: {
        'data-element': 'contact-left-column'
      }
    },
    
    // Right Column
    {
      id: rightColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Right Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactRightColumnClass,
      styles: {},
      children: [formWrapperId],
      text: '',
      attributes: {
        'data-element': 'contact-right-column'
      }
    },
    
    // Email Support
    {
      id: emailSupportId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Support',
      parentId: emailTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {},
      children: [],
      text: 'support@yourcompany.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Map Container
    {
      id: mapContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'map',
      name: 'Map Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: contactMapContainerClass,
      styles: {},
      children: [mapFrameId],
      text: '',
      attributes: {
        'data-element': 'contact-map-container'
      }
    },
    
    // Map Frame
    // {
    //   id: mapFrameId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'iframe',
    //   name: 'Map Frame',
    //   parentId: mapContainerId,
    //   hierarchyName: getHierarchyName('iframe'),
    //   className: contactMapFrameClass,
    //   styles: {},
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'contact-map-frame',
    //     'src': 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0958241203384!2d-122.41941378417224!3d37.7749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80858080a379575d%3A0x6cf7a313d6a52598!2sSan%20Francisco%2C%20CA%2094107!5e0!3m2!1sen!2sus!4v1654201834629!5m2!1sen!2sus',
    //     'allowfullscreen': 'true',
    //     'loading': 'lazy',
    //     'referrerpolicy': 'no-referrer-when-downgrade'
    //   }
    // },
    
    // Social Container
    {
      id: socialContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: contactSocialContainerClass,
      styles: {},
      children: [socialTitleId, socialLinksId],
      text: '',
      attributes: {
        'data-element': 'contact-social-container'
      }
    },
    
    // Social Title
    {
      id: socialTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Social Title',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('h4'),
      className: contactSocialTitleClass,
      styles: {},
      children: [],
      text: 'Connect With Us',
      attributes: {
        'data-element': 'contact-social-title'
      }
    },
    
    // Social Links
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactSocialLinksClass,
      styles: {},
      children: [facebookLinkId, twitterLinkId, instagramLinkId, linkedinLinkId],
      text: '',
      attributes: {
        'data-element': 'contact-social-links'
      }
    },
    
    // Facebook Link
    {
      id: facebookLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: contactSocialLinkClass,
      styles: {},
      children: [facebookIconId],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://facebook.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },
    
    // Facebook Icon
    {
      id: facebookIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: facebookLinkId,
      hierarchyName: getHierarchyName('image'),
      className: contactSocialIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/brands/facebook-f.svg',
      attributes: {
        'data-element': 'contact-social-icon',
        'alt': 'Facebook'
      }
    },
    
    // Twitter Link
    {
      id: twitterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: contactSocialLinkClass,
      styles: {},
      children: [twitterIconId],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://twitter.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },
    
    // Twitter Icon
    {
      id: twitterIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: twitterLinkId,
      hierarchyName: getHierarchyName('image'),
      className: contactSocialIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/brands/twitter.svg',
      attributes: {
        'data-element': 'contact-social-icon',
        'alt': 'Twitter'
      }
    },
    
    // Instagram Link
    {
      id: instagramLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Instagram Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: contactSocialLinkClass,
      styles: {},
      children: [instagramIconId],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://instagram.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },
    
    // Instagram Icon
    {
      id: instagramIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: instagramLinkId,
      hierarchyName: getHierarchyName('image'),
      className: contactSocialIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/brands/instagram.svg',
      attributes: {
        'data-element': 'contact-social-icon',
        'alt': 'Instagram'
      }
    },
    
    // LinkedIn Link
    {
      id: linkedinLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: contactSocialLinkClass,
      styles: {},
      children: [linkedinIconId],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://linkedin.com',
        'target': '_blank',
        'rel': 'noopener noreferrer'
      }
    },
    
    // LinkedIn Icon
    {
      id: linkedinIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: linkedinLinkId,
      hierarchyName: getHierarchyName('image'),
      className: contactSocialIconClass,
      styles: {},
      src: 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6/svgs/brands/linkedin-in.svg',
      attributes: {
        'data-element': 'contact-social-icon',
        'alt': 'LinkedIn'
      }
    },
    
    // Form Wrapper
    {
      id: formWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Wrapper',
      parentId: rightColumnId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormWrapperClass,
      styles: {},
      children: [formTitleId, formId, successMessageId, errorMessageId],
      text: '',
      attributes: {
        'data-element': 'contact-form-wrapper'
      }
    },
    
    // Form Title
    {
      id: formTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Form Title',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('h3'),
      className: contactFormTitleClass,
      styles: {},
      children: [],
      text: 'Send Us a Message',
      attributes: {
        'data-element': 'contact-form-title'
      }
    },
    
    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('form'),
      className: contactFormClass,
      styles: {},
      children: [nameEmailRowId, phoneSubjectRowId, messageGroupId, checkboxGroupId, submitButtonId],
      method: 'post',
      action: '',
      text: '',
      attributes: {
        'data-element': 'contact-form',
        'data-form-type': 'contact'
      }
    },
    
    // Name and Email Row
    {
      id: nameEmailRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name and Email Row',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormRowClass,
      styles: {},
      children: [nameGroupId, emailGroupId],
      text: '',
      attributes: {
        'data-element': 'form-row'
      }
    },
    
    // Name Field Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Field Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Full Name',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-name'
      }
    },
    
    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'text',
        'id': 'contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Field Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Field Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Email Address',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'email',
        'id': 'contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Phone and subject row
    {
      id: phoneSubjectRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone and Subject Row',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormRowClass,
      styles: {},
      children: [phoneGroupId, subjectGroupId],
      text: '',
      attributes: {
        'data-element': 'form-row'
      }
    },

    // Phone field
    // {
    //   id: phoneGroupId,
    //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    //   type: 'input',
    //   name: 'Phone Input',
    //   parentId: phoneSubjectRowId,
    //   hierarchyName: getHierarchyName('input'),
    //   className: contactFormInputClass,
    //   styles: {},
    //   children: [],
    //   text: '',
    //   attributes: {
    //     'data-element': 'form-input',
    //     'type': 'tel',
    //     'id': 'contact-phone',
    //     'name': 'phone',
    //     'placeholder': 'Enter your phone number'
    //   }
    // },

    // Subject field
    {
      id: subjectGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Subject Field Group',
      parentId: phoneSubjectRowId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [subjectLabelId, subjectSelectId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Subject label
    {
      id: subjectLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Subject Label',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Subject',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-subject'
      }
    },

    // Subject select
    {
      id: subjectSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'dropdown',
      name: 'Subject Select',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('select'),
      className: contactFormSelectClass,
      styles: {},
      children: [option1Id, option2Id, option3Id, option4Id],
      text: '',
      attributes: {
        'data-element': 'form-select',
        'id': 'contact-subject',
        'name': 'subject',
        'required': 'true'
      }
    },

    // Option 1
    {
      id: option1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Option 1',
      parentId: subjectSelectId,
      hierarchyName: getHierarchyName('option'),
      styles: {},
      children: [],
      text: 'Select a subject',
      attributes: {
        'data-element': 'form-option',
        'value': '',
        'disabled': 'true',
        'selected': 'true'
      }
    },

    // Option 2
    {
      id: option2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Option 2',
      parentId: subjectSelectId,
      hierarchyName: getHierarchyName('option'),
      styles: {},
      children: [],
      text: 'General Inquiry',
      attributes: {
        'data-element': 'form-option',
        'value': 'General Inquiry'
      }
    },

    // Option 3
    {
      id: option3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Option 3',
      parentId: subjectSelectId,
      hierarchyName: getHierarchyName('option'),
      styles: {},
      children: [],
      text: 'Technical Support',
      attributes: {
        'data-element': 'form-option',
        'value': 'Technical Support'
      }
    },

    // Option 4
    {
      id: option4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'option',
      name: 'Option 4',
      parentId: subjectSelectId,
      hierarchyName: getHierarchyName('option'),
      styles: {},
      children: [],
      text: 'Business Partnership',
      attributes: {
        'data-element': 'form-option',
        'value': 'Business Partnership'
      }
    },

    // Message field
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Message label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Your Message',
      attributes: {
        'data-element': 'form-label',
        'for': 'contact-message'
      }
    },

    // Message textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: contactFormTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-textarea',
        'id': 'contact-message',
        'name': 'message',
        'placeholder': 'Type your message here...',
        'required': 'true',
        'rows': '5'
      }
    },
    
    // Checkbox group
    {
      id: checkboxGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Checkbox Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormCheckboxClass,
      styles: {},
      children: [checkboxInputId, checkboxLabelId],
      text: '',
      attributes: {
        'data-element': 'form-checkbox-group'
      }
    },
    
    // Checkbox input
    {
      id: checkboxInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'checkbox',
      name: 'Checkbox Input',
      parentId: checkboxGroupId,
      hierarchyName: getHierarchyName('input'),
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-checkbox',
        'type': 'checkbox',
        'id': 'contact-consent',
        'name': 'consent',
        'required': 'true'
      }
    },
    
    // Checkbox label
    {
      id: checkboxLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Checkbox Label',
      parentId: checkboxGroupId,
      hierarchyName: getHierarchyName('label'),
      styles: {},
      children: [],
      text: 'I agree to the privacy policy and terms of service',
      attributes: {
        'data-element': 'form-checkbox-label',
        'for': 'contact-consent'
      }
    },

    // Submit button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: contactFormButtonClass,
      styles: {},
      children: [],
      text: 'Send Message',
      attributes: {
        'data-element': 'form-button',
        'type': 'submit'
      }
    },

    // Success message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormSuccessClass,
      styles: {},
      children: [],
      text: 'Thank you! Your message has been sent successfully. We will get back to you soon.',
      attributes: {
        'data-element': 'form-success-message'
      }
    },

    // Error message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormErrorClass,
      styles: {},
      children: [],
      text: 'Oops! There was an error sending your message. Please try again or contact us directly via phone or email.',
      attributes: {
        'data-element': 'form-error-message'
      }
    }
  ];
  
  return {
    contactSection: newElements[0],
    elements: newElements
  };
} 